import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Ban, X } from "lucide-react";
import { LGoButton } from "@/ui/branded";
import { AsyncBoundary } from "@/ui/patterns";
import {
  filterReservationView,
  filterReservationViewByOffer,
  partitionReservations,
  unfinishedCheckoutCount,
} from "@/domain/reservation/rules";
import type { PartnerReservation } from "@/domain/reservation/types";
import type { PartnerOffer } from "@/domain/offer/types";
import { formatDateTime } from "@/lib/time";
import { CancelOfferDialog } from "@/features/offers/components/CancelOfferDialog";
import { useCancelOffer } from "@/features/offers/hooks/usePartnerOffers";
import { useOfferById } from "../hooks/useOfferById";
import { useReservationList } from "../hooks/useReservationList";
import { ReservationQueue } from "./ReservationQueue";
import { ReservationList } from "./ReservationList";

function offerIdFrom(params: URLSearchParams): number | null {
  const asked = params.get("offer");
  if (asked === null || !/^\d+$/.test(asked)) return null;
  return Number(asked);
}

/** Named from the sleva itself rather than from a row inside the filtered view:
 *  an offer nobody has bought is exactly when the partner cannot infer which
 *  one they are looking at, and that is the view with no row to read. */
function nameOffer(offer: PartnerOffer | undefined): string {
  if (!offer) return "vybraný slevový termín";
  return `${offer.activityName}, ${formatDateTime(offer.startTime)}`;
}

export function ReservationsScreen() {
  const { reservations, isLoading, error, refetch } = useReservationList();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const offerId = offerIdFrom(searchParams);
  const [isCancellingOffer, setIsCancellingOffer] = useState(false);

  const filteredOffer = useOfferById(offerId);
  const cancelOffer = useCancelOffer();

  // `now` is read once per batch of reservations, never per keystroke: a row
  // crosses from Nadcházející to Uzavřené on a refetch, not while somebody types.
  const partitioned = useMemo(
    () => partitionReservations(reservations, new Date()),
    [reservations],
  );
  const onOffer = useMemo(
    () => filterReservationViewByOffer(partitioned, offerId),
    [partitioned, offerId],
  );
  const matching = useMemo(() => filterReservationView(onOffer, searchTerm), [onOffer, searchTerm]);
  // Counted before the search narrows anything: a place held by an unfinished
  // checkout belongs to the sleva, not to what the partner has typed.
  const unfinishedCheckouts = useMemo(
    () => (filteredOffer ? unfinishedCheckoutCount(onOffer, filteredOffer.reserved) : 0),
    [onOffer, filteredOffer],
  );
  const isSearching = searchTerm.trim() !== "";

  const dropOfferFilter = () => {
    const withoutOffer = new URLSearchParams(searchParams);
    withoutOffer.delete("offer");
    setSearchParams(withoutOffer, { replace: true });
  };

  // The filter names a sleva that is gone the moment this succeeds, so the
  // partner is handed the whole list back instead of an empty filtered one.
  const confirmOfferCancellation = () => {
    if (!filteredOffer) return;
    cancelOffer.mutate(filteredOffer.id, {
      onSuccess: dropOfferFilter,
      onSettled: () => setIsCancellingOffer(false),
    });
  };

  return (
    <div className="space-y-10">
      {offerId !== null && (
        <div className="flex flex-wrap items-center gap-2">
          <LGoButton
            variant="secondary"
            size="sm"
            iconRight={<X />}
            onClick={dropOfferFilter}
            aria-label={`Zrušit filtr: ${nameOffer(filteredOffer)}`}
          >
            {`Filtr: ${nameOffer(filteredOffer)}`}
          </LGoButton>
          {filteredOffer && (
            <LGoButton
              variant="destructive"
              size="sm"
              iconLeft={<Ban />}
              onClick={() => setIsCancellingOffer(true)}
            >
              Zrušit celý slevový termín
            </LGoButton>
          )}
        </div>
      )}
      <AsyncBoundary<PartnerReservation[]>
        data={reservations}
        isLoading={isLoading}
        error={error}
        onRetry={() => void refetch()}
      >
        {() => (
          <div className="space-y-10">
            <ReservationQueue queue={matching.awaitingAnswer} isSearching={isSearching} />
            <ReservationList
              view={matching}
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              hasMatchInQueue={matching.awaitingAnswer.length > 0}
              unfinishedCheckouts={unfinishedCheckouts}
            />
          </div>
        )}
      </AsyncBoundary>
      <CancelOfferDialog
        offer={isCancellingOffer ? (filteredOffer ?? null) : null}
        onOpenChange={(open) => !open && setIsCancellingOffer(false)}
        onConfirm={confirmOfferCancellation}
        isCancelling={cancelOffer.isPending}
      />
    </div>
  );
}
